import { LogOut } from 'lucide-react';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, UserData } from '../hooks/auth';
import { Button } from '../shadcn/Button';
import { embedded_sale_url, get_embedded_sale_context, LOGIN_PATH } from '../utils/embeddedSale';
import Logo from './Logo';
import '../embedded-sale.css';

export default function EmbeddedSaleLayout({ children, userData }: { children: ReactNode; userData: UserData | null }) {
	const navigate = useNavigate();
	const scroll_ref = useRef<HTMLDivElement>(null);
	const [signing_out, setSigningOut] = useState(false);

	useLayoutEffect(() => {
		const root = document.documentElement;
		root.dataset.embeddedSale = 'true';
		const viewport = window.visualViewport;
		const update_viewport = () => {
			root.style.setProperty('--pos-viewport-height', `${viewport?.height || window.innerHeight}px`);
			const focused = document.activeElement;
			if (focused instanceof HTMLElement && scroll_ref.current?.contains(focused)) {
				focused.scrollIntoView({ block: 'nearest' });
			}
		};
		update_viewport();
		viewport?.addEventListener('resize', update_viewport);
		window.addEventListener('resize', update_viewport);
		return () => {
			delete root.dataset.embeddedSale;
			root.style.removeProperty('--pos-viewport-height');
			viewport?.removeEventListener('resize', update_viewport);
			window.removeEventListener('resize', update_viewport);
		};
	}, []);

	return (
		<div className="pos-layout flex min-h-0 w-full flex-col bg-background text-neutral-100">
			<header className="flex shrink-0 items-center gap-3 border-b border-neutral-800 px-4 py-2">
				<div role="img" aria-label="Krece" className="w-20 shrink-0">
					<Logo className="h-auto w-full" />
				</div>
				<div className="min-w-0 flex-1 text-sm">
					<p className="truncate font-medium" title={userData?.store_name || 'Venta con Krece'}>
						{userData?.store_name || 'Venta con Krece'}
					</p>
					{userData?.email && (
						<p className="truncate text-xs text-neutral-400" title={userData.email}>
							{userData.email}
						</p>
					)}
				</div>
				{userData && (
					<Button
						variant="secondary"
						className="!w-auto shrink-0 px-3"
						aria-label="Cerrar sesión"
						title="Cerrar sesión"
						loading={signing_out}
						onClick={() => {
							const context = get_embedded_sale_context();
							const login_url = context ? embedded_sale_url(LOGIN_PATH, context) : LOGIN_PATH;
							setSigningOut(true);
							logout(() => navigate(login_url, { replace: true }));
						}}
					>
						<LogOut className="size-4" />
					</Button>
				)}
			</header>
			<div ref={scroll_ref} data-embedded-sale-scroll className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
				{children}
			</div>
		</div>
	);
}
